import { useEffect, useState } from 'react'

import { useHistory } from 'react-router-dom'
import { BottomSheet, Button, Tag, Text, formatToBRL } from 'components/InterUI/InterUI'
import BaseBridge from 'config/bridge/BaseBridge'
import { CheckoutBaseBridge } from 'config/bridge/CheckoutBaseBridge'
import { useLocalErrorHandler } from 'hooks/useLocalErrorHandler'
import { WaitingPayment } from 'pages/landingPage/Home/WaitingPayment/WaitingPayment'
import { Payment } from 'pages/landingPage/Home/Payment/Payment'
import { AnalyticsLandingPage } from 'pages/landingPage/Home/Home.tags'
import { TypesRoutes } from 'routes/mixedRoutes/types'
import useBoundState from 'store'
import { Icons } from 'utils/icons'

import { Flex } from '@interco/inter-ui/components/Flex'

import {
  analyticsPurchaseBottomSheetDisplay,
  analyticsPurchaseBottomSheetPay,
} from './PurchaseBottomSheet.tags'
import * as S from './PurchaseBottomSheet.styles'

interface Props {
  onClose: () => void
  restoreBackListener?: () => void
}

const SINGLE_TICKET_PRICE = 3

export const PurchaseBottomSheet = ({ onClose, restoreBackListener }: Props) => {
  const history = useHistory()
  const handlerError = useLocalErrorHandler()

  const contentCapitalization = useBoundState((state) => state.contentCapitalization)
  const { enabled } = useBoundState((state) => state.eligibility)
  const formOfPayment = useBoundState((state) => state.formOfPayment)
  const isLoading = useBoundState((state) => state.isLoading)
  const emptyFormOfPayment = useBoundState((state) => state.emptyFormOfPayment)
  const getCheckoutResponse = useBoundState((state) => state.getCheckoutResponse)
  const getEligibility = useBoundState((state) => state.getEligibility)

  const [quantitySelected, setQuantitySelected] = useState(1)
  const [payout, setPayout] = useState(SINGLE_TICKET_PRICE)
  const [eligibilityBS, setEligibilityBS] = useState(false)
  const [inWaitPayment, setInWaitPayment] = useState(false)

  const canceledIcon = <Icons.Canceled width={32} height={32} color="var(--error500)" />

  useEffect(() => {
    getEligibility()
    analyticsPurchaseBottomSheetDisplay()
  }, [])

  useEffect(() => {
    BaseBridge.addBackListener(inWaitPayment ? () => undefined : onClose)

    return () => {
      if (!inWaitPayment) {
        restoreBackListener?.()
      }
    }
  }, [onClose, restoreBackListener, inWaitPayment])

  const handleCheckout = async () => {
    if (enabled === false) {
      setEligibilityBS(true)
      return
    }

    analyticsPurchaseBottomSheetPay(formatToBRL(payout))

    try {
      const result = await CheckoutBaseBridge.contractWallet(`${payout}`)

      emptyFormOfPayment()
      setInWaitPayment(true)

      getCheckoutResponse({ history, capitalizationLot: result.transactionId })
    } catch (error) {
      const errorMessage = error as Error

      if (errorMessage.message !== 'walletCheckoutManagerDidUserCancelISafeAuthentication') {
        handlerError(errorMessage, 'PurchaseBottomSheet.handleCheckout')
      }
    }
  }

  const openGeneralConditions = () => {
    AnalyticsLandingPage({ action_id: 'condições gerais', ref_type: 'botão' })
    BaseBridge.openPdf(
      'https://static.bancointer.com.br/seguros/capitalization/CondicoesGeraisSortezinha.pdf',
      'Condições Gerais',
    )
  }

  const openRegistrationForm = () => {
    AnalyticsLandingPage({ action_id: 'ficha de cadastro', ref_type: 'botão' })
    history.push(TypesRoutes.REGISTRATION_FORM)
  }

  const openLegalInfo = () => {
    AnalyticsLandingPage({ action_id: 'informações legais', ref_type: 'botão' })
    BaseBridge.openPdf(
      'https://static.bancointer.com.br/seguros/capitalization/InformacoesLegaisSortezinha.pdf',
      'Informações Legais',
    )
  }

  const renderEligibilityBS = eligibilityBS && (
    <BottomSheet noSwipe onClose={() => setEligibilityBS(false)}>
      <S.BottomSheetContent>
        {canceledIcon}
        <Text variant="headline-h3" as="h3" semiBold>
          Não foi possível concluir sua compra
        </Text>
        <Text variant="body-3">
          A compra da {contentCapitalization.value1} não está disponível para a sua conta.
        </Text>
        <Button fullWidth variant="primary" onClick={() => setEligibilityBS(false)}>
          Entendi
        </Button>
      </S.BottomSheetContent>
    </BottomSheet>
  )

  if (inWaitPayment) {
    return <WaitingPayment />
  }

  return (
    <>
      <BottomSheet onClose={onClose}>
        <S.Content data-testid="purchase-bottom-sheet">
          <S.Header>
            <Tag theme="green">Por apenas R$ 3,00</Tag>
            <Text variant="headline-h1" as="h1" semiBold>
              Garanta sua próxima chance!
            </Text>
            <Text variant="body-3">
              Compre uma nova {contentCapitalization.value1} e continue concorrendo a prêmios na
              hora e nos sorteios.
            </Text>
          </S.Header>

          <Payment
            key="purchaseBottomSheetPayment"
            quantitySelected={quantitySelected}
            payout={payout}
            setQuantitySelected={setQuantitySelected}
            setPayout={setPayout}
            singleTicketPrice={SINGLE_TICKET_PRICE}
          />

          <S.Checkout>
            <Flex direction="column" justifyContent="center">
              <Text variant="caption-1" colorWeight={500}>
                Total a pagar
              </Text>
              <Text variant="headline-h3" as="h3" semiBold color="primary">
                {formatToBRL(payout)}
              </Text>
            </Flex>

            <Button
              data-testid="purchase-pay-button"
              fullWidth
              disabled={!formOfPayment.type || enabled === undefined}
              isLoading={isLoading}
              onClick={handleCheckout}
            >
              Pagar
            </Button>
          </S.Checkout>

          <Text variant="caption-1" colorWeight={500}>
            Ao continuar declaro estar de acordo com{' '}
            <S.Link onClick={openGeneralConditions}>Condições Gerais</S.Link>,{' '}
            <S.Link onClick={openLegalInfo}>Informações legais</S.Link> e{' '}
            <S.Link onClick={openRegistrationForm}>Ficha de Cadastro</S.Link> da{' '}
            {contentCapitalization.value1}.
          </Text>
        </S.Content>
      </BottomSheet>

      {renderEligibilityBS}
    </>
  )
}
